import RosterBase from './Roster.vue'
import { CLEARS_TIMES_IDS } from './rosterAttendanceKinds'
import {
  STAMPED_KIND_PENDING,
  STAMPED_KIND_SETTLED,
  STAMPED_KIND_WORKING,
  clockToMinutes,
  endClearedRestFactsOf,
  minutesToClock,
  settledStampedOriginOf,
  stampedNightOriginOf,
  stampedOriginOf
} from '../utils/rosterStampedRow'

const hasTimeValue = value => value !== null && value !== undefined && String(value) !== ''

// 時刻の文字を時・分で比べる（「01:30」と「01:30:00」は同じ）。
const sameTime = (left, right) =>
  String(left == null ? '' : left).slice(0, 5) === String(right == null ? '' : right).slice(0, 5)

// その行が打刻由来の行なら、その印（無ければ null）。
const stampedOriginAt = (vm, index) => (
  Array.isArray(vm.stampedOrigins) ? (vm.stampedOrigins[index] || null) : null
)

// その行の夜勤休憩の印（無ければ null）。休憩を手で直しても外さない（stampedNightOriginOf）。
const stampedNightOriginAt = (vm, index) => (
  Array.isArray(vm.stampedNightOrigins) ? (vm.stampedNightOrigins[index] || null) : null
)

/*
 * 打刻由来の行で、勤務表の休憩の自動計算が走る直前に当てる決まり。
 *
 * 休憩終了の打刻は、夜の休憩（22〜5時の休憩）が無くても、夜勤休憩へ 00:00 を入れる。
 * 勤務表で時刻を入れた日は、夜勤休憩を空のまま保存する。同じ結果にするため、
 * 夜の休憩を打刻していない行の 00:00 は、空へ戻す。夜の休憩を打刻した行は、そのまま残す。
 *
 * 読み込みの最後に打ち直す日（退勤まで打刻した日）にも、出勤だけ打刻した日に勤務表で終了を
 * 入れたときにも、ここ1か所が当たる（どちらも autoRestTime から呼ぶ）。
 *
 * 勤務時間の計算まで記録された打刻日（処理済みの打刻日）には当てない。その日の夜勤休憩は、
 * 1度保存された値なので、時刻を直しただけでは変えない。
 */
function clearUnstampedNightRest (vm, index) {
  if (nightRestIsUnstampedZero(vm, index)) {
    vm.$set(vm.nightRest, index, '')
  }
}

/*
 * 開始と終了がそろった行だけ、clearUnstampedNightRest を当てる。
 *
 * 時刻欄の変更（autoRestTime）で空へ戻す処理に当たるのも、開始と終了がそろった行だけ（そろっていなければ、
 * autoRestTime の最初の分岐で抜ける）。同じ条件・同じ関数を、次の2か所からも呼ぶ。
 * - ファイル取り込みで開始か終了が変わった行（clearUnstampedNightRestAt。Roster.vue の取り込みの仕上げ）。
 * - 読み込みの最後に、休憩を手で直した打刻日を打ち直すとき（reworkStampedRows）。
 */
function clearUnstampedNightRestWhenTimed (vm, index) {
  if (!hasTimeValue(vm.start[index]) || !hasTimeValue(vm.end[index])) return
  clearUnstampedNightRest(vm, index)
}

/*
 * その行の夜勤休憩 00:00 が、「入力ではない値」（夜の休憩を打刻していない、打刻由来の行の 00:00）か。
 *
 * 見分けは、ここ1か所。次の2つが、同じこの答えを使う（判定の写しを作らない）。
 * - 休憩の自動計算の前に、夜勤休憩を空へ戻す所（clearUnstampedNightRest）。
 * - ファイル取り込みで、夜勤休憩を「空欄」と同じに扱う所（nightRestCountsAsBlankForImport）。
 *
 * 見る印は、夜勤休憩の印（stampedNightOrigins。utils/rosterStampedRow.js の stampedNightOriginOf）。
 * 休憩の印（stampedOrigins）は見ない。休憩の印は、休憩を手で直した時点で外れ、読み込みのときも
 * 「休憩と夜勤休憩が打刻の入れた値のままか」（stamped_rest_untouched）が偽なら付かない。それを見ると、
 * 休憩という別の欄を直しただけで、夜勤休憩の 00:00 が「利用者が入れた値」に変わってしまう
 * （保存して開き直したあとも）。2026-10-03 までそうなっていた。
 *
 * false を返す行（その夜勤休憩は、入力済みの値）：
 * - 打刻由来でない行（勤務表で入れて保存した行・印が付いてこない応答）。
 * - この画面で、勤務表の夜勤休憩の欄へ値を入れた行・取り込みで夜勤休憩が欄に入った行（markNightRestEntered が印を外す）。
 * - 勤務時間の計算まで記録された打刻日（処理済みの打刻日。夜勤休憩の印は付かない）。
 * - 夜の休憩を打刻した行。
 * - 夜勤休憩が 00:00 でない行（空の行・00:00 より大きい値を入れた行）。
 */
function nightRestIsUnstampedZero (vm, index) {
  const origin = stampedNightOriginAt(vm, index)
  if (!origin || origin.kind === STAMPED_KIND_SETTLED) return false
  return origin.nightBreakMinutes === 0 && clockToMinutes(vm.nightRest[index]) === 0
}

/*
 * 勤務表の自動計算が出した休憩（'HH:mm'）へ、打刻にしか無い事実を重ねた値（分）。
 *
 * 1. 固定休憩を使わない会社は、案件の休憩を引く（定時後休憩の分だけが残る）。
 * 2. 打刻した休憩の合計を足す。
 *
 * 重ねる所（applyStampedRest）と、読み込みのあとに「保存済みの休憩は、重ねた値のままか」を
 * 比べる所（markPersistedRestOverrides）が、同じこの1つを使う。
 */
function stampedRestOver (vm, index, origin, autoRest) {
  let rest = clockToMinutes(String(autoRest == null ? '' : autoRest).slice(0, 5)) || 0
  if (!origin.fixedBreak) {
    rest = Math.max(0, rest - (clockToMinutes(vm.breakBasic[index]) || 0))
  }
  return rest + origin.breakMinutes
}

/*
 * 打刻由来の行で、勤務表の休憩の自動計算が走った直後に当てる決まり。
 *
 * 勤務表の自動計算は、休憩を「案件の休憩＋定時後休憩」に作り直す。打刻した日には、
 * 打刻にしか無い事実（休憩の打刻、固定休憩を使わない会社）があるので、作り直した値へそれを重ねる。
 * 毎回、自動計算が案件の休憩から作り直した値を出発点にするので、何度走っても同じ値になる。
 *
 * 1. 固定休憩を使わない会社は、案件の休憩を引く（定時後休憩の分だけが残る）。
 * 2. 打刻した休憩の合計を足す。
 * 3. その行の勤務時間（画面が出している値）が負なら、その分だけ休憩を減らす。足りなければ夜勤休憩も減らす。
 *    打刻は「休憩が働いた時間を超える」退勤を断れないので、超えた分の休憩を記録しない。
 *    勤務時間の式はここに写さない。画面が出した値を物差しにする。
 *
 * 3 は、勤務時間の計算まで記録された打刻日（処理済みの打刻日）には当てない。
 * 処理済みの日の時刻を直すのは、勤務表での入力。休憩が働いた時間を超える入力は、勤務表で入れた日と
 * 同じに、保存のときに断られる（黙って休憩を減らして、通してしまわない）。
 * 休憩を減らすのは、打刻をそのまま記録するとき（まだ計算されていない日・出勤だけ打刻した日）だけ。
 */
function applyStampedRest (vm, index) {
  const origin = stampedOriginAt(vm, index)
  if (!origin) return

  // 自動計算が休憩を入れなかった（開始か終了が空）なら、重ねるものも無い。
  if (clockToMinutes(vm.rest[index]) === null) return

  let rest = stampedRestOver(vm, index, origin, vm.rest[index])
  vm.rest.splice(index, 1, minutesToClock(rest))

  // 処理済みの打刻日は、休憩が働いた時間を超えても減らさない（勤務表で入れた日と同じに、保存で断られる）。
  if (origin.kind === STAMPED_KIND_SETTLED) return

  const work = Array.isArray(vm.dailyWorkResult) ? clockToMinutes(vm.dailyWorkResult[index]) : null
  if (work === null || work >= 0) return

  let shortage = -work
  const cut = Math.min(rest, shortage)
  rest -= cut
  shortage -= cut
  vm.rest.splice(index, 1, minutesToClock(rest))
  if (shortage <= 0) return

  const nightRest = clockToMinutes(vm.nightRest[index])
  if (nightRest !== null && nightRest > 0) {
    vm.nightRest.splice(index, 1, minutesToClock(Math.max(0, nightRest - shortage)))
  }
}

/*
 * 出勤だけ打刻した行で、終了が空のまま休憩の自動計算が走った直後に当てる決まり。
 *
 * 勤務表の自動計算は、開始か終了が空の行の休憩を空にする。出勤を打刻した日は終了が空なので、
 * 勤務表で開始を直すだけで、打刻が入れた休憩が空になって保存される。そのあと退勤を打刻すると、
 * 休憩が空の行は「休憩を手で直した日」と見分けが付かず、休憩なしで記録されてしまう。
 *
 * そこで、出勤だけ打刻した行は、終了が空のあいだ、休憩を打刻が入れた値へ戻す。
 * 固定休憩を使う会社は「案件の休憩＋打刻した休憩」、使わない会社は「打刻した休憩」。
 *
 * 開始も終了も保存してあった行の終了を消したときも、同じ値へ戻す（endClearedRestFactsAt）。
 *
 * - 開始が空になった（開始を消した）ときは戻さない（今までどおり空）。
 * - 打刻が入れた休憩が 0 分のときは戻すものが無い（今までどおり空）。
 * - 夜勤休憩の欄は触らない。勤務表は終了が空でも夜勤休憩を消さないので、戻すものが無い。
 *   利用者がこの欄へ入れた値を、打刻の値で上書きもしない。
 */
function restoreStampedRestWhileWorking (vm, index) {
  if (!hasTimeValue(vm.start[index]) || hasTimeValue(vm.end[index])) return

  const origin = stampedOriginAt(vm, index)
  // 出勤だけ打刻した行。そうでなければ、開始も終了も保存してあった行の終了を消したとき（下の説明）。
  const facts = origin && origin.kind === STAMPED_KIND_WORKING ? origin : endClearedRestFactsAt(vm, index)
  if (!facts) return

  const fixed = facts.fixedBreak ? (clockToMinutes(vm.breakBasic[index]) || 0) : 0
  const stamped = fixed + facts.breakMinutes
  if (stamped <= 0) return
  vm.rest.splice(index, 1, minutesToClock(stamped))
}

/*
 * 開始も終了も保存してあった行の終了を、勤務表で消したときに、休憩を戻す先の事実（無ければ null）。
 *
 * 退勤を押し間違えた日は、勤務表で終了を消して保存し、あとで退勤を打刻する。終了を消したときに
 * 休憩が空になって保存されると、退勤のあと、休憩なしで記録されてしまう（休憩を手で空にした日と
 * 見分けが付かないため）。出勤だけ打刻した日と同じ値へ戻しておけば、同じ時刻を打刻しただけの日と
 * 同じ結果になる。
 *
 * 当てる行の判定は utils/rosterStampedRow.js（endClearedRestFactsOf）。打刻の印が付いてこない応答・
 * 行が無い日・勤務表で開始だけ入れた行には当たらない（今までどおり、休憩を空にする）。
 */
function endClearedRestFactsAt (vm, index) {
  const row = Array.isArray(vm.items) ? vm.items[index] : null
  if (!row) return null
  return endClearedRestFactsOf(row, { future: vm.isFutureIndex(index) })
}

/*
 * 終了がまだ無い行で、保存済みの休憩が「手で直した値」か。
 *
 * 出勤を打刻した日は、打刻が休憩（固定休憩＋打刻した休憩）を入れる。その値のままなら、
 * あとから終了を入れたときに計算し直す。利用者が勤務表でその休憩を直して保存した日は、
 * 終了を入れても、直した休憩を上書きしない（退勤を打刻して終えた日と同じ結果にする）。
 *
 * 見分けは、一覧の行に付いてくる「休憩が打刻の入れた値のままか」の印でする。
 * - 印が「ままではない」行：手で直した値。
 * - 印が「まま」の行、印が付いてこない応答（印を返さない版のサーバ）：手で直した値とは見ない（今までどおり）。
 * - 時刻を隠して返された行・画面が未来日として扱う行：見ない（入力できない行）。
 */
function restEditedBeforeEnd (vm, index) {
  const row = Array.isArray(vm.items) ? vm.items[index] : null
  if (!row || row.stamped_rest_untouched !== false) return false
  if (row.actual_hidden === true || vm.isFutureIndex(index)) return false
  return hasTimeValue(vm.start[index]) && !hasTimeValue(vm.end[index])
}

/*
 * 開始も終了もある保存済みの行が、「処理済みの打刻日」で、休憩が打刻の事実を重ねた値のままなら、その印。
 *
 * 打刻して、勤務時間の計算まで記録された日は、休憩に打刻した休憩の分が足されている
 * （固定休憩を使わない会社は、案件の休憩が入っていない）。自動の値とは違うが、利用者が直した値ではない。
 * 保存済みの休憩が「自動の値（auto）へ、打刻の事実を重ねた値」と同じなら、印を返す。
 *
 * null を返す行（今までどおり、自動の値と違う休憩として固定される）：
 * - 打刻の印が付いてこない応答の行、重ねる事実が無い行（判定は utils/rosterStampedRow.js）。
 * - 重ねた値とも違う休憩の行（手で直した休憩。休憩が働いた時間を超えて減らした日も、ここに入る）。
 * - 画面が未来日として扱う行。
 */
function settledStampedRestAt (vm, index, auto) {
  const row = Array.isArray(vm.items) ? vm.items[index] : null
  const settled = settledStampedOriginOf(row, { hasStart: true, hasEnd: true, future: false })
  if (!settled || vm.isFutureIndex(index)) return null
  const expected = minutesToClock(stampedRestOver(vm, index, settled, auto))
  return sameTime(vm.rest[index], expected) ? settled : null
}

/**
 * 勤務表の実績休憩を自動候補より優先するラッパー。
 *
 * 定時直後休憩は会社設定から初期値として反映するが、電話対応・待機・作業等が
 * あった日に利用者が休憩欄を実績へ直した後は、終了時刻やプロジェクト基準の
 * 再取得で上書きしない。管理者用勤務表も同じコンポーネントを使う。
 *
 * ## 打刻した日の扱い（2026-10-01）
 *
 * 打刻は開始・終了・休憩しか記録しない。打刻した日を、勤務表で同じ時刻を入れた日と
 * 同じ結果にするため、「打刻由来の行」（判定は utils/rosterStampedRow.js）にだけ、次の決まりを当てる。
 * 見える勤務表でも、画面に出さずに動かすとき（RosterHeadless.js）でも、動くのはここ。
 *
 * - 勤務時間がまだ計算されていない日は、読み込みの最後に、時刻欄へ入力したときと同じ
 *   休憩の自動計算をやり直す（reworkStampedRows）。
 * - 出勤だけ打刻した日は、打刻が入れた休憩を「手で直した休憩」として固定しない
 *   （終了が空の行は固定しない、という markPersistedRestOverrides の決まりに含まれる）。
 * - 打刻由来の行で自動計算が走るたびに、打刻した休憩を重ねる（applyStampedRest）。
 *   夜の休憩を打刻していない行は、夜勤休憩の 00:00 を空へ戻す（clearUnstampedNightRest）。
 *   夜勤休憩の 00:00 が打刻の入れた値かは、休憩を手で直したかに関係なく決める（stampedNightOrigins）。
 *   時刻欄を変えたとき・ファイル取り込みで開始か終了が変わったとき・休憩を直した打刻日を読み込んだときに当たる。
 * - 出勤だけ打刻した日は、終了が空のまま開始を直しても、打刻が入れた休憩を消さない
 *   （restoreStampedRestWhileWorking）。
 * - 開始も終了も保存してあった日の終了を消したときも、休憩を空にせず、出勤だけ打刻した日と同じ値へ戻す
 *   （退勤を押し間違えた日を、終了を消して保存し、あとで退勤を打刻する人のため）。
 * - 勤務時間の計算まで記録された打刻日（処理済みの打刻日）も、休憩が「自動の値へ、打刻の事実を
 *   重ねた値」のままなら固定せず、自動計算が走るたびに、打刻した休憩を重ねる
 *   （settledStampedRestAt）。読み込んだだけでは値を変えない。夜勤休憩も空へ戻さない。
 * - 休憩欄へ利用者が値を入れた行は、その時点から打刻由来の扱いをやめる。
 *
 * 打刻の印が付いてこない応答では、打刻由来の行は1つも無く、今までと同じに動く。
 */
export default {
  name: 'Roster',
  extends: RosterBase,

  data () {
    return {
      manualRestOverrides: [],
      // 行ごとの、打刻由来の行の印（違う行は null）。読み込みのたびに決め直す。
      stampedOrigins: [],
      // 行ごとの、夜勤休憩の印（違う行は null）。読み込みのたびに決め直す。休憩を手で直しても外さない。
      stampedNightOrigins: []
    }
  },

  watch: {
    // 保存済みの日次休憩は実績値として扱う。読込完了後にだけ固定することで、
    // 新規行の自動候補は引き続き開始・終了変更に追随できる。
    // 順番を変えない：打刻由来の行を決める → 保存済みの休憩を固定する → 打刻した日を打ち直す。
    // 打ち直しは固定の直後に走り、打刻した日の固定を外す。
    loading (value) {
      if (!value && this.displayFlg) {
        this.decideStampedOrigins()
        this.markPersistedRestOverrides()
        this.reworkStampedRows()
      }
    }
  },

  methods: {
    // 読み込みの始めに、前の表の「手で直した休憩」の固定と打刻の印を空にしてから、本体の読み込みを流す。
    // 空にしないと、読み込みのあいだ（幕の向こう）、前の月の同じ行番号の固定と印が次の表に残り、
    // 「実績へ手動修正済み」が見えたり、前の月の打刻した休憩が足されたりした。
    // 読み込みが終わったあとの決め直し（下の watch の loading）と、固定の決まりは変えない。
    callList () {
      this.manualRestOverrides = []
      this.stampedOrigins = []
      this.stampedNightOrigins = []
      return RosterBase.methods.callList.call(this)
    },

    setManualRestOverride (index, value) {
      if (!Number.isInteger(index) || index < 0) return
      this.$set(this.manualRestOverrides, index, Boolean(value))
      // 手で直した休憩になった行は、その時点から打刻由来の扱いをやめる。
      if (value && stampedOriginAt(this, index)) {
        this.$set(this.stampedOrigins, index, null)
      }
    },

    // その行の休憩を「手で直した休憩」として固定しているか。
    // 休憩の欄の下の文（実績へ手動修正済み／定時後休憩を自動反映）は、これに合わせて出る。
    isRestManual (index) {
      return Boolean(this.manualRestOverrides[index])
    },

    // ファイル取り込みで、その行の休憩を「空欄」と同じに扱うか。
    // 出勤だけ打刻した日（終了がまだ無く、休憩が打刻の入れた値のまま）は、空欄と同じに扱う。
    // その休憩は、利用者が入力した値ではない。「空欄だけに反映」でも、ファイルに休憩が書いてあれば、
    // その値を入れる（入れた休憩は、ファイルに書いた実績として固定する。applyRosterFileImport）。
    // 休憩の欄へ利用者が値を入れた行・打刻のあと休憩を直して保存した行は、打刻由来の行ではないので、
    // 入力済みのまま。打刻の印が付いてこない応答でも、入力済みのまま（今までどおり）。
    restCountsAsBlankForImport (index) {
      const origin = stampedOriginAt(this, index)
      if (!origin || origin.kind !== STAMPED_KIND_WORKING) return false
      return hasTimeValue(this.start[index]) && !hasTimeValue(this.end[index])
    },

    // ファイル取り込みで、その行の夜勤休憩を「空欄」と同じに扱うか。
    // 休憩終了の打刻は、夜の休憩が無くても、夜勤休憩へ 00:00 を入れる。その 00:00 は、利用者が
    // 入力した値ではない（勤務表も、休憩の自動計算の前に空へ戻している）。「空欄だけに反映」でも、
    // ファイルに夜勤休憩が書いてあれば、その値を入れる。
    // 判定は、夜勤休憩を空へ戻す所と同じ1つ（nightRestIsUnstampedZero）。
    // 夜の休憩を打刻した行・打刻由来でない行・00:00 でない値は、入力済みのまま。
    // 打刻の印が付いてこない応答でも、入力済みのまま（今までどおり）。
    nightRestCountsAsBlankForImport (index) {
      return nightRestIsUnstampedZero(this, index)
    },

    // 読み込んだ行のうち、どれが打刻由来の行かを決める。
    // 休憩の印（stampedOrigins）と夜勤休憩の印（stampedNightOrigins）を、同じ一覧の行・同じ画面の値から決める。
    decideStampedOrigins () {
      const rows = Array.isArray(this.items) ? this.items : []
      const optionsAt = index => ({
        hasStart: hasTimeValue(this.start[index]),
        hasEnd: hasTimeValue(this.end[index]),
        future: this.isFutureIndex(index)
      })
      this.stampedOrigins = rows.map((row, index) => stampedOriginOf(row, optionsAt(index)))
      this.stampedNightOrigins = rows.map((row, index) => stampedNightOriginOf(row, optionsAt(index)))
    },

    // 開始・終了がそろった行で、打刻が入れた夜勤休憩 00:00（夜の休憩は打刻していない）を空へ戻す。
    // ファイル取り込みで開始か終了が変わった行（Roster.vue の取り込みの仕上げ）が呼ぶ。
    // 時刻欄の変更（autoRestTime）が通るのと同じ関数・同じ条件。休憩を手で直した行でも、
    // 休憩の補完を外した取り込みでも、同じに当たる。
    clearUnstampedNightRestAt (index) {
      clearUnstampedNightRestWhenTimed(this, index)
    },

    // 夜勤休憩の欄へ、利用者が値を入れた（勤務表の手入力・ファイル取り込み）。その値は打刻が入れた値ではないので、
    // 夜勤休憩の印を外す（休憩の欄の setManualRestOverride と同じ扱い）。以後、その行の 00:00 は、時刻を変えても
    // 空へ戻さず、「空欄だけに反映」でも入力済みと数える。
    // 外さないと、勤務表で「終了 → 夜勤休憩 00:00」と打てば 00:00、「夜勤休憩 00:00 → 終了」なら空、ファイルに
    // 終了と夜勤休憩 00:00 を書いて取り込めば空、と入れ方で保存の本文が分かれていた（2026-10-03）。
    // 勤務表の夜勤休憩の欄の変更（onNightRestChange）と、取り込みで夜勤休憩が欄に入った行（Roster.vue）が、ここを呼ぶ。
    markNightRestEntered (index) {
      if (!Number.isInteger(index) || index < 0) return
      if (stampedNightOriginAt(this, index)) {
        this.$set(this.stampedNightOrigins, index, null)
      }
    },

    onNightRestChange (index) {
      this.markNightRestEntered(index)
      return RosterBase.methods.onNightRestChange.call(this, index)
    },

    // その行の開始・終了から、休憩の自動計算が出す値。画面の値は変えない。
    // 式を写さず、勤務表の本体の autoRestTime そのものを、休憩の配列だけ控えに
    // 差し替えて流す（本体の式が変われば、ここも同じに変わる）。
    autoRestCandidate (index) {
      const probe = Object.create(this)
      Object.defineProperty(probe, 'rest', {
        value: Array.isArray(this.rest) ? this.rest.slice() : [],
        writable: true
      })
      RosterBase.methods.autoRestTime.call(probe, index)
      return probe.rest[index]
    },

    // 保存済みの休憩のうち、固定するのは「手で直した値」だけ。
    //
    // 保存済みというだけで固定すると、自動で入った休憩まで固定されて、あとから
    // 時刻を入れても直しても定時直後休憩が入らない（2026-08-07 から 10-02 まで
    // そうなっていた）。固定しないのは次の2つ。
    //
    // 1. 終了がまだ無い日。ダッシュボードで出勤を打刻すると、その時点で固定休憩が
    //    日次へ入る（終了は空）。あとから勤務表で終了を入れたら、休憩を計算し直す。
    //    autoRestTime が、開始か終了が空の行の固定を外すのと同じ条件。
    // 2. 保存済みの休憩が、その日の開始・終了から自動で出る値と同じ日。
    //    利用者が直した値ではないので、終了を直せば休憩もついていく。
    //
    // 自動の値と違う休憩（電話対応・待機などで実績へ直した値）は、今までどおり固定する。
    //
    // 1 には例外が1つある（restEditedBeforeEnd）。終了がまだ無くても、開始があって、
    // 一覧の行の印が「休憩は打刻の入れた値のままではない」と言っている行は、手で直した値として固定する。
    // 出勤を打刻 → 勤務表で休憩を直して保存 → 勤務表で終了を入れる、で直した休憩が消えないように。
    // 印が付いてこない応答では、例外は当たらない（1 のまま）。
    //
    // 出勤だけ打刻した日・退勤まで打刻してまだ計算されていない日のための決まりは、ここには置かない。
    // - 出勤だけ打刻した日（休憩が打刻の入れた値のまま）は終了が空なので、1 で固定されない。
    // - 退勤まで打刻して勤務時間がまだ計算されていない日は、休憩が自動の値と違えば
    //   ここでいったん固定され、直後の reworkStampedRows が固定を外して打ち直す。
    // - 打刻のあと勤務表で休憩を直した日は打刻由来の行ではないので、ここで固定されたまま残る。
    //
    // 自動の値（autoRestCandidate）は本体の式だけで出す。打刻した休憩の足し直し
    // （applyStampedRest）は含めない。比べたいのは「勤務表が自動で入れる値」だから。
    //
    // 2 には、打刻した日のための続きがある（settledStampedRestAt）。打刻して、勤務時間の計算まで
    // 記録された日は、休憩に打刻した休憩の分が足されている（固定休憩を使わない会社は、案件の休憩が
    // 入っていない）ので、自動の値とは違う。保存済みの休憩が「自動の値へ、打刻の事実を重ねた値」と
    // 同じなら、利用者が直した値ではないので固定しない。その行は、休憩の自動計算が走るたびに、
    // 同じ事実を重ね直す行（処理済みの打刻日）として印を付ける。重ねた値とも違う休憩は、固定する。
    // 打刻の印が付いてこない応答では、続きは当たらない（2 のまま）。
    //
    // 休憩が「働いた時間ちょうど」まで縮めてある行（業務時間が 0:00 で、自動の値のほうが大きい行）も、
    // 自動の値と違えば、手で直した休憩として固定する（master と同じ。2026-10-03、監査 SR-1）。
    // 保存済みの値だけでは、打刻が縮めた休憩（出勤のあとすぐ退勤を押した日）と、勤務表で休憩を
    // 手で直して保存した休憩（例：13:10〜13:55・休憩 00:45）を見分けられない。後者の固定を外すと、
    // 終了だけを直したときに、直した休憩が自動の値で上書きされる。手で直した休憩を守る側を取る。
    // 打刻が縮めた日を自動へ戻すのは、利用者の明示の操作（終了か開始を消してから入れ直す）。
    // まだ勤務時間が計算されていない打刻日（stamping_pending）は、ここでいったん固定されても、
    // 直後の reworkStampedRows が固定を外して打ち直す（今までどおり）。
    markPersistedRestOverrides () {
      const rowIds = Array.isArray(this.id) ? this.id : []
      const starts = Array.isArray(this.start) ? this.start : []
      const ends = Array.isArray(this.end) ? this.end : []
      rowIds.forEach((rowId, index) => {
        const saved = rowId != null && hasTimeValue(this.rest[index])
        const complete = hasTimeValue(starts[index]) && hasTimeValue(ends[index])
        let manual = false
        if (saved && !complete) {
          manual = restEditedBeforeEnd(this, index)
        } else if (saved) {
          const auto = this.autoRestCandidate(index)
          if (!sameTime(this.rest[index], auto)) {
            const settled = settledStampedRestAt(this, index, auto)
            if (settled) {
              this.$set(this.stampedOrigins, index, settled)
            } else {
              manual = true
            }
          }
        }
        this.$set(this.manualRestOverrides, index, manual)
      })
    },

    // 打刻して、勤務時間がまだ計算されていない日を、時刻欄へ入力したときと同じ計算でやり直す。
    //
    // 休憩の印が無く、夜勤休憩の印だけが「まだ計算されていない日」の行（出勤を打刻 → 勤務表で休憩を直して
    // 保存 → 退勤を打刻、の日）は、休憩は手で直した値のまま、夜勤休憩 00:00 だけを空へ戻す。
    // 勤務表で同じ日に終了を打ったとき（autoRestTime。手で直した休憩でも、夜勤休憩は先に空へ戻す）と
    // 同じ結果にするため。見える勤務表を開いたときも、打刻のあとの見えない保存（RosterHeadless.js）も、ここを通る。
    reworkStampedRows () {
      this.stampedOrigins.forEach((origin, index) => {
        if (!origin || origin.kind !== STAMPED_KIND_PENDING) return
        this.setManualRestOverride(index, false)
        // 時刻欄の変更（@change）が呼ぶのと同じメソッド。
        // 夜の休憩を打刻していない行の夜勤休憩 00:00 を空へ戻すのも、この中（clearUnstampedNightRest）。
        this.autoRestTime(index)
      })
      ;(Array.isArray(this.stampedNightOrigins) ? this.stampedNightOrigins : []).forEach((nightOrigin, index) => {
        if (stampedOriginAt(this, index) || !nightOrigin || nightOrigin.kind !== STAMPED_KIND_PENDING) return
        clearUnstampedNightRestWhenTimed(this, index)
      })
    },

    onUpdateRest ({ index, value }) {
      RosterBase.methods.onUpdateRest.call(this, { index, value })
      this.setManualRestOverride(index, true)
    },

    autoRestTime (index, options = {}) {
      const force = Boolean(options && options.force)

      // 勤務時刻自体を消した場合は、休憩だけを残さない。
      if (!this.start[index] || !this.end[index]) {
        this.setManualRestOverride(index, false)
        RosterBase.methods.autoRestTime.call(this, index)
        // 出勤だけ打刻した行は、終了が空のあいだ、打刻が入れた休憩を消さない。
        // 開始も終了も保存してあった行の終了を消したときも、同じ値へ戻す。
        restoreStampedRestWhileWorking(this, index)
        return true
      }

      // 打刻由来の行は、夜の休憩を打刻していなければ、夜勤休憩の 00:00 を空へ戻してから計算する。
      // 休憩を手で直した行でも戻す（下の固定の判定より前）。固定の判定の後ろに置いていたときは、
      // 「休憩 → 終了」の順に打つと夜勤休憩が 00:00 のまま、「終了 → 休憩」の順なら空で保存され、
      // 勤務表の手入力どうしでも結果がずれていた（2026-10-03）。
      // 夜勤休憩の欄の変更から来たとき（keepNightRest）は戻さない。利用者が打っている最中の欄を
      // 空へ戻すと、00:05 のような値が打てない（分の1桁目を打った時点で 00:00 になる）。
      // （ここに来るのは開始と終了がそろった行だけ。取り込みが呼ぶ clearUnstampedNightRestAt と同じ関数）
      if (!(options && options.keepNightRest)) clearUnstampedNightRest(this, index)

      // 手動で実績へ修正した休憩は、定時・案件基準の再取得で上書きしない。
      if (!force && this.manualRestOverrides[index]) {
        return false
      }

      RosterBase.methods.autoRestTime.call(this, index)
      // 打刻由来の行は、作り直した休憩へ、打刻した休憩を重ねる。
      applyStampedRest(this, index)
      return true
    },

    applyDefaultAt (index) {
      // 「定時を反映」は利用者が明示した再計算操作なので、自動候補へ戻す。
      this.setManualRestOverride(index, false)
      return RosterBase.methods.applyDefaultAt.call(this, index)
    },

    autoTime (index) {
      // 時刻を空にする区分（休日・終日の休み）。並びは勤怠区分の表に1か所で持つ。
      if (CLEARS_TIMES_IDS.includes(this.attendance1[index])) {
        this.setManualRestOverride(index, false)
      }
      return RosterBase.methods.autoTime.call(this, index)
    },

    applyRosterFileImport (payload) {
      const before = Array.isArray(this.rest) ? this.rest.slice() : []
      // 取り込みの前に、休憩を「空欄」と同じに扱っていた行（出勤だけ打刻した日）。
      const blankBefore = Array.isArray(this.importRestAsBlank) ? this.importRestAsBlank.slice() : []
      const result = RosterBase.methods.applyRosterFileImport.call(this, payload)

      // ファイルへ明示された休憩は実績入力として扱う。スケジュールからの
      // 自動補完はロックせず、その後の時刻変更へ追随させる。
      this.$nextTick(() => {
        ;((payload && payload.rows) || []).forEach(row => {
          if (!row || !Number.isInteger(row.index)) return
          const values = row.values || {}
          if (!Object.prototype.hasOwnProperty.call(values, 'rest')) return
          const after = this.rest[row.index]
          const changed = String(before[row.index] ?? '') !== String(after ?? '')
          // 出勤だけ打刻した日へ、打刻が入れた値と同じ休憩が書いてあるファイルを取り込んだとき。
          // 値は変わらないが、ファイルに書いた休憩なので、実績として固定する
          // （固定しないと、あとで終了を直したときに、ファイルの休憩が計算し直されてしまう）。
          const sameAsStamped = blankBefore[row.index] === true &&
            hasTimeValue(after) && sameTime(after, values.rest)
          if (changed || sameAsStamped) {
            this.setManualRestOverride(row.index, true)
          }
        })
      })
      return result
    }
  }
}
